import type { PropsWithChildren } from 'react'
import { useLocation } from 'react-router-dom'

import { useFeatures } from '@jotta/grpc-connect-client/config'
import { useFaceGroupingConsentQuery } from '@jotta/grpc-connect-client/people/faceGroupingConsent'
import type { Photos } from '@jotta/types/Photos'
import { lazyWithRetry } from '@jotta/ui/lazyWithRetry'

import { AlbumSectionContainer } from '../AlbumsSection/AlbumSectionContainer'

const LazyFaceGroupingConsentSection = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FaceGroupingConsentSection" */ '../../FaceGroupingConsent/FaceGroupingConsentSection'
  ).then(module => ({
    default: module.FaceGroupingConsentSection,
  })),
)

const LazyPeopleSectionContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PeopleSectionContainer" */
    '../People/PeopleSectionContainer'
  ).then(module => ({
    default: module.PeopleSectionContainer,
  })),
)

export interface AlbumListProps {
  showOnly?: Photos.DisplayCollectionTypeKey
}

export function AlbumListFrame({ children }: PropsWithChildren) {
  return (
    <div className="relative">
      <div className="flex w-full flex-col gap-3 overflow-hidden px-2 pb-16 pt-4 opacity-[var(--content-opacity,1)] transition-opacity duration-300 sm:px-3 lg:px-4">
        {children}
      </div>
    </div>
  )
}

function PeopleSectionFeature() {
  const { facesV2 } = useFeatures()
  const faceGroupingConsentQuery = useFaceGroupingConsentQuery({
    enabled: facesV2,
  })

  if (!facesV2 || faceGroupingConsentQuery.isPending) {
    return null
  }

  const allowFaceGrouping = faceGroupingConsentQuery.data === true

  if (allowFaceGrouping) {
    return <LazyPeopleSectionContainer />
  }

  return <LazyFaceGroupingConsentSection />
}

export function AlbumList({ showOnly }: AlbumListProps) {
  const { pathname } = useLocation()

  return (
    <AlbumListFrame>
      {showOnly ? (
        <AlbumSectionContainer
          albumType={showOnly}
          showAll
          backPath={pathname}
        />
      ) : (
        <>
          <PeopleSectionFeature />
          <AlbumSectionContainer albumType="NORMAL_ALBUM" />
          <AlbumSectionContainer albumType="META_SHARED_ALBUM_TYPES" />
          <AlbumSectionContainer albumType="LOCATION_ALBUM" />
        </>
      )}
    </AlbumListFrame>
  )
}
